import * as React from "react";
import { PromptGeneratorContext } from "@/components/feature/promptGenerator/context";
import { type PromptGeneratorContextState } from "@/components/feature/promptGenerator/types";

interface UsePromptGeneratorContext extends PromptGeneratorContextState {
  sendPromptToChat: (prompt: string | null) => void;
  sendPromptToImprove: (prompt: string | null) => void;
  markChatSendComplete: () => void;
  markMessageSentToChat: (messageId: string) => void;
}

export const usePromptGeneratorContext = (): UsePromptGeneratorContext => {
  const context = React.useContext(PromptGeneratorContext);

  if (!context) {
    throw new Error("usePromptGeneratorContext must be used within a PromptGeneratorProvider");
  }
  const { state, setState } = context;

  const sendPromptToImprove = React.useCallback(
    (prompt: string | null) => {
      setState(prev => ({
        ...prev,
        promptToImprove: prompt,
      }));
    },
    [setState],
  );

  const sendPromptToChat = React.useCallback(
    (prompt: string | null) => {
      setState(prev => ({
        ...prev,
        promptToChat: prompt,
        isSendingToChat: prompt !== null ? true : prev.isSendingToChat,
      }));
    },
    [setState],
  );

  const markChatSendComplete = React.useCallback(() => {
    setState(prev => (prev.isSendingToChat ? { ...prev, isSendingToChat: false } : prev));
  }, [setState]);

  const markMessageSentToChat = React.useCallback(
    (messageId: string) => {
      setState(prev => ({
        ...prev,
        sentChatMessageIds: new Set([...prev.sentChatMessageIds, messageId]),
      }));
    },
    [setState],
  );

  return {
    promptToImprove: state.promptToImprove,
    promptToChat: state.promptToChat,
    isSendingToChat: state.isSendingToChat,
    sentChatMessageIds: state.sentChatMessageIds,
    sendPromptToChat,
    sendPromptToImprove,
    markChatSendComplete,
    markMessageSentToChat,
  };
};
